"use client";

import { c as _c } from "react/compiler-runtime";
import ChevronDownIcon from "@/assets/icons/chevron_down.svg";
import ChevronUpIcon from "@/assets/icons/chevron_up.svg";
import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import styles from "./expandable-list.module.css";
const DEFAULT_MAX_COLLAPSED_HEIGHT = 150;
export type ExpandableListProps = Readonly<{
  items?: readonly string[];
  children?: ReactNode;
  maxCollapsedHeight?: number;
  expandLabel?: string;
  collapseLabel?: string;
}>;
export function ExpandableList(t0) {
  const $ = _c(30);
  const {
    items,
    children,
    maxCollapsedHeight: t1,
    expandLabel: t2,
    collapseLabel: t3
  } = t0;
  const maxCollapsedHeight = t1 === undefined ? DEFAULT_MAX_COLLAPSED_HEIGHT : t1;
  const expandLabel = t2 === undefined ? "See More" : t2;
  const collapseLabel = t3 === undefined ? "See Less" : t3;
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentId = useId();
  const contentRef = useRef(null);
  const hasOverflow = contentHeight > maxCollapsedHeight;
  const isExpanded = expanded && hasOverflow;
  const clampedHeight = isExpanded ? contentHeight : maxCollapsedHeight;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const content = contentRef.current;
      if (!content) {
        return;
      }
      const measure = () => {
        const height = content.scrollHeight;
        setContentHeight(prev => prev === height ? prev : height);
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(content);
      return () => observer.disconnect();
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== children || $[2] !== items) {
    t5 = [items, children];
    $[1] = children;
    $[2] = items;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  useLayoutEffect(t4, t5);
  const t6 = hasOverflow ? "true" : undefined;
  const t7 = isExpanded ? "true" : undefined;
  let t8;
  if ($[4] !== clampedHeight || $[5] !== hasOverflow) {
    t8 = hasOverflow ? {
      "--see-more-height": `${clampedHeight}px`
    } as CSSProperties : undefined;
    $[4] = clampedHeight;
    $[5] = hasOverflow;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== children || $[8] !== items) {
    t9 = children != null ? children : <ul className={styles.bulletList}>{(items ?? []).map(_temp)}</ul>;
    $[7] = children;
    $[8] = items;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== contentId || $[11] !== t9) {
    t10 = <div ref={contentRef} id={contentId}>{t9}</div>;
    $[10] = contentId;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== hasOverflow) {
    t11 = hasOverflow ? <div className={styles.listFade} aria-hidden={true} /> : null;
    $[13] = hasOverflow;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t12 = <div className={styles.visibleListWrap} data-clamped={t6} data-expanded={t7} style={t8}>{t10}{t11}</div>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== collapseLabel || $[22] !== contentId || $[23] !== expandLabel || $[24] !== hasOverflow || $[25] !== isExpanded) {
    t13 = hasOverflow ? <button type="button" className={styles.toggle} onClick={() => setExpanded(_temp2)} aria-expanded={isExpanded} aria-controls={contentId}>{isExpanded ? <ChevronUpIcon className={styles.toggleIcon} aria-hidden={true} /> : <ChevronDownIcon className={styles.toggleIcon} aria-hidden={true} />}<span className={styles.toggleLabel}>{isExpanded ? collapseLabel : expandLabel}</span></button> : null;
    $[21] = collapseLabel;
    $[22] = contentId;
    $[23] = expandLabel;
    $[24] = hasOverflow;
    $[25] = isExpanded;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = <div className={styles.root}>{t12}{t13}</div>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}
function _temp2(prev_0) {
  return !prev_0;
}
function _temp(item, index) {
  return <li key={`${item}-${index}`}><span className={styles.listItemText}>{item}</span></li>;
}